/**
 * EntityAssetExtractor - Unified entity extraction from GLB files
 * 
 * Works for BOTH maps and character GLBs. Entities are identified by `hm_behaviors`
 * on root objects. Child meshes and animations are automatically associated via 
 * Blender's parent hierarchy.
 * 
 * Entity types are derived from the behavior category:
 * - trigger:* behaviors → 'trigger' type
 * - spawn:* behaviors → 'spawn' type  
 * - character:* behaviors → 'character' type
 * - weapon:* behaviors → 'weapon' type
 * etc.
 * 
 * Reference syntax: `#entityId` to reference embedded entities in configs
 * 
 * NOTE: This extracts TEMPLATES from GLB files.
 * Runtime entities are registered in EntityRegistry (src/core/EntityRegistry.ts).
 */

import { AbstractMesh, AnimationGroup, Skeleton, TransformNode, AssetContainer, Node } from '@babylonjs/core';
import { logger } from '../core/Logger';

// Property prefix matching Blender addon
const ENTITY_PROPERTY_PREFIX = 'hm';

/** 
 * Represents an entity extracted from a GLB file.
 * This is a generic structure that works for ALL entity types.
 */
export interface ExtractedEntity {
  /** Entity type derived from behavior category (e.g., 'trigger', 'character', 'weapon') */
  type: string;
  /** Unique identifier - from hm_entity_id if present, otherwise generated from node name */
  id: string;
  /** 
   * Behaviors from hm_behaviors (comma-separated string in Blender).
   * Format: "behavior1,behavior2" where behaviors can be:
   * - Presets: "character:lilly", "weapon:sword"
   * - Mixins: "physics", "damageable", "explosive"
   */
  behaviors: string[];
  /** Root TransformNode/Mesh for this entity */
  root: TransformNode;
  /** All meshes under this entity hierarchy */
  meshes: AbstractMesh[];
  /** Skeleton if the entity has an armature */
  skeleton: Skeleton | null;
  /** Animations targeting nodes within this entity, keyed by Blender action name */
  animations: Map<string, AnimationGroup>;
  /** Raw extras object for type-specific property access */
  extras: Record<string, any>;
}

/** Result of extraction containing all entities found in the container */
export interface ExtractionResult {
  /** Map of entity ID to extracted entity */
  entities: Map<string, ExtractedEntity>;
  /** Container this was extracted from */
  container: AssetContainer;
}

/**
 * Get a property from gltf extras with the hm_ prefix
 */
export function getEntityProperty<T = any>(extras: any, propPath: string, defaultValue: T): T {
  if (!extras) return defaultValue;
  const key = `${ENTITY_PROPERTY_PREFIX}_${propPath.replace(/\./g, '_')}`;
  return extras[key] ?? defaultValue;
}

/**
 * Get extras from a node (mesh or transform)
 */
export function getExtras(node: Node): any {
  const metadata = node.metadata;
  if (!metadata) return null;
  
  if (metadata.gltf?.extras) return metadata.gltf.extras;
  if (metadata.extras) return metadata.extras;
  return null;
}

/**
 * Parse a behavior string into type and id parts.
 * 
 * @example
 * parseBehavior("character:lilly") => { type: "character", id: "lilly" }
 * parseBehavior("physics") => { type: "mixin", id: "physics" }
 * parseBehavior("cave_light") => { type: "trigger", id: "cave_light" }
 */
export function parseBehavior(behavior: string): { type: string; id: string } {
  const colonIndex = behavior.indexOf(':');
  if (colonIndex !== -1) {
    return {
      type: behavior.substring(0, colonIndex),
      id: behavior.substring(colonIndex + 1),
    };
  }
  // No colon means it's a mixin or trigger behavior
  return { type: 'behavior', id: behavior };
}

/**
 * Check if entity has a specific behavior or behavior type
 */
export function hasBehavior(entity: ExtractedEntity, behaviorOrType: string): boolean {
  return entity.behaviors.some(b => {
    const parsed = parseBehavior(b);
    return b === behaviorOrType || 
           parsed.type === behaviorOrType || 
           parsed.id === behaviorOrType;
  });
}

/**
 * Get the primary behavior for an entity (first in list, or type-specific)
 */
export function getPrimaryBehavior(entity: ExtractedEntity): string | null {
  return entity.behaviors[0] ?? null;
}

/**
 * Check if a TransformNode is a descendant of another node
 */
function isDescendantOf(child: Node, parent: Node): boolean {
  let current = child.parent;
  while (current) {
    if (current === parent) return true;
    current = current.parent;
  }
  return false;
}

/**
 * Strip Blender's automatic naming suffixes (.001, .002, etc.)
 */
function stripBlenderSuffix(name: string): string {
  return name.replace(/\.\d{3}$/, '');
}

/**
 * Extract all entities from an AssetContainer.
 * Works for both maps and character GLBs.
 * 
 * @param container - The loaded AssetContainer to scan
 * @param options - Optional configuration
 * @returns ExtractionResult with all entities found
 * @throws If duplicate hm_entity_id found
 */
export function extractEntities(
  container: AssetContainer,
  options?: {
    /** Entity types that require hm_entity_id (throws if missing). Default: [] (none required) */
    requireIdForTypes?: string[];
  }
): ExtractionResult {
  // No types require explicit ID by default - we use node name as fallback
  const requireIdForTypes = options?.requireIdForTypes ?? [];
  
  const entities = new Map<string, ExtractedEntity>();
  const entityRoots = new Map<string, TransformNode>(); // ID -> root node
  
  // Collect all TransformNodes and Meshes
  const allNodes: TransformNode[] = [
    ...container.transformNodes,
    ...container.meshes,
  ];
  
  // Find all entity roots (nodes with hm_behaviors)
  for (const node of allNodes) {
    const extras = getExtras(node);
    
    // Get behaviors from hm_behaviors
    const behaviorsRaw = getEntityProperty<string>(extras, 'behaviors', '');
    const behaviors = behaviorsRaw
      ? behaviorsRaw.split(',').map(b => b.trim()).filter(b => b.length > 0)
      : [];
    
    // Skip nodes without behaviors
    if (behaviors.length === 0) {
      continue;
    }
    
    // Derive entity type from first behavior's category
    // 'weapon:bass' → type='weapon', 'weapon' → type='weapon', 'trigger:gravity' → type='trigger'
    const firstBehavior = behaviors[0];
    const colonIndex = firstBehavior.indexOf(':');
    const entityType = colonIndex !== -1 
      ? firstBehavior.substring(0, colonIndex)  // e.g., 'trigger' from 'trigger:gravity_zone'
      : firstBehavior;  // e.g., 'weapon' from 'weapon' (no colon = behavior IS the type)
    
    // Generate or get entity ID - fallback to node name
    let entityId = getEntityProperty<string>(extras, 'entity_id', '');
    
    // If behavior has explicit ID after colon, use that
    if (!entityId && colonIndex !== -1) {
      const behaviorId = firstBehavior.substring(colonIndex + 1);
      if (behaviorId.length > 0) {
        entityId = behaviorId;
      }
    }
    
    // Validate entity_id is present for types that require it
    if (!entityId && requireIdForTypes.includes(entityType)) {
      throw new Error(
        `Entity with behavior '${firstBehavior}' at node '${node.name}' is missing required hm_entity_id property`
      );
    }
    
    // If no ID, generate one from node name
    if (!entityId) {
      entityId = stripBlenderSuffix(node.name);
    }
    
    // Check for duplicate IDs
    if (entities.has(entityId)) {
      throw new Error(
        `Duplicate hm_entity_id '${entityId}' found. First at '${entityRoots.get(entityId)!.name}', second at '${node.name}'`
      );
    }
    
    logger.assets.debug(`Found entity root: ${node.name}, type=${entityType}, id=${entityId}, behaviors=${behaviors.join(',')}`);
    
    // Collect child meshes
    const childMeshes: AbstractMesh[] = [];
    if (node instanceof AbstractMesh) {
      childMeshes.push(node);
    }
    for (const child of node.getChildMeshes(false)) {
      childMeshes.push(child);
    }
    
    // Find skeleton (if armature is a child)
    let skeleton: Skeleton | null = null;
    for (const mesh of childMeshes) {
      if (mesh.skeleton) {
        skeleton = mesh.skeleton;
        break;
      }
    }
    
    // Create extracted entity with all extras for type-specific access
    const extractedEntity: ExtractedEntity = {
      type: entityType,
      id: entityId,
      behaviors,
      root: node,
      meshes: childMeshes,
      skeleton,
      animations: new Map(),
      extras: extras ?? {},
    };
    
    entities.set(entityId, extractedEntity);
    entityRoots.set(entityId, node);
  }
  
  // Associate animations with entities
  for (const animGroup of container.animationGroups) {
    // Find which entity this animation belongs to by checking targets
    let ownerEntityId: string | null = null;
    
    for (const targetedAnim of animGroup.targetedAnimations) {
      const target = targetedAnim.target;
      if (!target) continue;
      
      // Check if target is a descendant of any entity root
      for (const [entityId, root] of entityRoots) {
        if (target === root || isDescendantOf(target, root)) {
          if (ownerEntityId && ownerEntityId !== entityId) {
            // Animation targets multiple entities - this is an error
            throw new Error(
              `Animation '${animGroup.name}' targets nodes in multiple entities: '${ownerEntityId}' and '${entityId}'. ` +
              `Animations must only target nodes within a single entity hierarchy.`
            );
          }
          ownerEntityId = entityId;
          break;
        }
      }
      
      // If target is not in any entity, check if we have an owner already
      if (!ownerEntityId) {
        // Target is outside all entity hierarchies
        // This is only an error if the animation seems to belong to an entity
        // For now, we skip animations that don't target any entity
        continue;
      }
    }
    
    if (ownerEntityId) {
      const entity = entities.get(ownerEntityId)!;
      
      // Use animation name as key (this is the Blender action name)
      const animName = animGroup.name;
      
      if (entity.animations.has(animName)) {
        logger.assets.warn(
          `Duplicate animation name '${animName}' for entity '${ownerEntityId}'. Using last occurrence.`
        );
      }
      
      entity.animations.set(animName, animGroup);
      logger.assets.debug(`Associated animation '${animName}' with entity '${ownerEntityId}'`);
    }
  }
  
  // Log summary
  logger.assets.log(
    `Extracted ${entities.size} entities: ` +
    Array.from(entities.values())
      .map(e => `${e.id}(${e.type}: ${e.meshes.length} meshes, ${e.animations.size} anims)`)
      .join(', ')
  );
  
  return { entities, container };
}

/**
 * Check if a reference string is an embedded entity reference
 * Embedded references start with '#'
 */
export function isEmbeddedReference(ref: string): boolean {
  return ref.startsWith('#');
}

/**
 * Parse an embedded reference string
 * Format: '#entityId' or '#entityId:animationName'
 */
export function parseEmbeddedReference(ref: string): { entityId: string; animationName?: string } {
  if (!isEmbeddedReference(ref)) {
    throw new Error(`Invalid embedded reference: '${ref}'. Must start with '#'`);
  }
  
  const withoutHash = ref.slice(1);
  const colonIndex = withoutHash.indexOf(':');
  
  if (colonIndex === -1) {
    return { entityId: withoutHash };
  }
  
  return {
    entityId: withoutHash.slice(0, colonIndex),
    animationName: withoutHash.slice(colonIndex + 1),
  };
}

/**
 * Get an entity by ID from an extraction result. Throws if not found.
 */
export function getEntity(result: ExtractionResult, entityId: string): ExtractedEntity {
  const entity = result.entities.get(entityId);
  if (!entity) {
    throw new Error(
      `Entity '${entityId}' not found. Available: [${Array.from(result.entities.keys()).join(', ')}]`
    );
  }
  return entity;
}